import { create } from "zustand/react";
import {
  getCertificates,
  getCompetency,
  getStatisticPerCompetency,
  getBadges,
  getTimeSpent,
  getProgressBar,
  getLeadersBoard,
  getPioneersClub,
} from "../axios/statistics";
import {
  CompetencyItem,
  StatisticItem,
  TimeSpentItem,
  ProgressItem,
  LeaderBoardItem,
  LeaderBoardResponse,
  PioneerClubItem,
} from "@/types/statistics";

let progressBarInflight: Promise<void> | null = null;
let leadersBoardInflight: Promise<void> | null = null;
let pioneersClubInflight: Promise<void> | null = null;

interface StatisticsStore {
  certificates: string[];
  isLoadingCertificates: boolean;
  errorCertificates: string | null;
  fetchCertificates: () => Promise<void>;

  competencies: CompetencyItem[];
  lastCompetencies: CompetencyItem | null;
  isLoadingCompetencies: boolean;
  errorCompetencies: string | null;
  fetchCompetencies: () => Promise<void>;
  setLastCompetency: (competency: CompetencyItem) => void;

  statistic: StatisticItem[];
  isLoadingStatistic: boolean;
  errorStatistic: string | null;
  fetchStatistic: (competency: string) => Promise<void>;

  badges: string[];
  isLoadingBadges: boolean;
  errorBadges: string | null;
  fetchBadges: () => Promise<void>;

  timeSpent: TimeSpentItem[];
  isLoadingTimeSpent: boolean;
  errorTimeSpent: string | null;
  fetchTimeSpent: () => Promise<void>;

  progressBar: ProgressItem[];
  isLoadingProgressBar: boolean;
  errorProgressBar: string | null;
  refreshedProgressBar: boolean;
  fetchProgressBar: () => Promise<void>;
  setRefreshedProgressBar: (refreshedProgressBar: boolean) => Promise<void>;

  leadersBoardText: string;
  leadersBoard: LeaderBoardItem[];
  isLoadingLeadersBoard: boolean;
  errorLeadersBoard: string | null;
  refreshedLeaderBoard: boolean;
  fetchLeadersBoard: () => Promise<void>;
  setRefreshedLeaderBoard: (refreshedLeaderBoard: boolean) => Promise<void>;

  pioneersClub: PioneerClubItem[];
  isLoadingPioneersClub: boolean;
  errorPioneersClub: string | null;
  fetchPioneersClub: () => Promise<void>;

  clearStatistics: () => void;
}

export const useStatisticsStore = create<StatisticsStore>((set) => ({
  certificates: [],
  isLoadingCertificates: false,
  errorCertificates: null,
  fetchCertificates: async () => {
    set({ isLoadingCertificates: true, errorCertificates: null });
    try {
      const data = await getCertificates();
      set({ certificates: data, isLoadingCertificates: false });
    } catch (error) {
      set({
        errorCertificates: "Error fetching certificates",
        isLoadingCertificates: false,
      });
    }
  },

  competencies: [],
  lastCompetencies: null,
  isLoadingCompetencies: false,
  errorCompetencies: null,
  fetchCompetencies: async () => {
    set({ isLoadingCompetencies: true, errorCompetencies: null });
    try {
      const data = await getCompetency();
      set({ competencies: data, isLoadingCompetencies: false });
    } catch (error) {
      set({
        errorCompetencies: "Error fetching competencies",
        isLoadingCompetencies: false,
      });
    }
  },
  setLastCompetency: (competency: CompetencyItem) =>
    set({ lastCompetencies: competency }),

  statistic: [],
  isLoadingStatistic: false,
  errorStatistic: null,
  fetchStatistic: async (competency: string) => {
    set({ isLoadingStatistic: true, errorStatistic: null });
    try {
      const data = await getStatisticPerCompetency(competency);
      set({ statistic: data, isLoadingStatistic: false });
    } catch (error) {
      set({
        errorStatistic: "Error fetching statistic for competency",
        isLoadingStatistic: false,
      });
    }
  },

  badges: [],
  isLoadingBadges: false,
  errorBadges: null,
  fetchBadges: async () => {
    set({ isLoadingBadges: true, errorBadges: null });
    try {
      const data = await getBadges();
      set({ badges: data, isLoadingBadges: false });
    } catch (error) {
      set({
        errorBadges: "Error fetching badges",
        isLoadingBadges: false,
      });
    }
  },

  timeSpent: [],
  isLoadingTimeSpent: false,
  errorTimeSpent: null,
  fetchTimeSpent: async () => {
    set({ isLoadingTimeSpent: true, errorTimeSpent: null });
    try {
      const data: TimeSpentItem[] = await getTimeSpent();
      set({ timeSpent: data, isLoadingTimeSpent: false });
    } catch (error) {
      set({
        errorTimeSpent: "Error fetching time spent",
        isLoadingTimeSpent: false,
      });
    }
  },

  progressBar: [],
  isLoadingProgressBar: false,
  errorProgressBar: null,
  refreshedProgressBar: true,
  fetchProgressBar: async () => {
    if (progressBarInflight) return progressBarInflight;

    progressBarInflight = (async () => {
      set({ isLoadingProgressBar: true, errorProgressBar: null });
      try {
        const data = await getProgressBar();
        set({ progressBar: data, isLoadingProgressBar: false });
      } catch (error) {
        set({
          errorProgressBar: "Error fetching progress bar",
          isLoadingProgressBar: false,
        });
      } finally {
        progressBarInflight = null;
      }
    })();

    return progressBarInflight;
  },
  setRefreshedProgressBar: async (refreshedProgressBar) => {
    set({ refreshedProgressBar: refreshedProgressBar });
  },

  leadersBoardText: "",
  leadersBoard: [],
  isLoadingLeadersBoard: false,
  errorLeadersBoard: null,
  refreshedLeaderBoard: true,
  fetchLeadersBoard: async () => {
    if (leadersBoardInflight) return leadersBoardInflight;

    leadersBoardInflight = (async () => {
      set({ isLoadingLeadersBoard: true, errorLeadersBoard: null });
      try {
        const data: LeaderBoardResponse = await getLeadersBoard();
        set({
          leadersBoardText: data.text,
          leadersBoard: data.board,
          isLoadingLeadersBoard: false,
        });
      } catch (error) {
        set({
          errorLeadersBoard: "Error fetching leaders board",
          isLoadingLeadersBoard: false,
        });
      } finally {
        leadersBoardInflight = null;
      }
    })();

    return leadersBoardInflight;
  },
  setRefreshedLeaderBoard: async (refreshedLeaderBoard) => {
    set({ refreshedLeaderBoard: refreshedLeaderBoard });
  },

  pioneersClub: [],
  isLoadingPioneersClub: false,
  errorPioneersClub: null,
  fetchPioneersClub: async () => {
    if (pioneersClubInflight) return pioneersClubInflight;

    pioneersClubInflight = (async () => {
      set({ isLoadingPioneersClub: true, errorPioneersClub: null });
      try {
        const data = await getPioneersClub();
        set({ pioneersClub: data, isLoadingPioneersClub: false });
      } catch (error) {
        set({
          errorPioneersClub: "Error fetching pioneers club",
          isLoadingPioneersClub: false,
        });
      } finally {
        pioneersClubInflight = null;
      }
    })();

    return pioneersClubInflight;
  },

  clearStatistics: () =>
    set({
      certificates: [],
      isLoadingCertificates: false,
      errorCertificates: null,

      competencies: [],
      lastCompetencies: null,
      isLoadingCompetencies: false,
      errorCompetencies: null,

      statistic: [],
      isLoadingStatistic: false,
      errorStatistic: null,

      badges: [],
      isLoadingBadges: false,
      errorBadges: null,

      timeSpent: [],
      isLoadingTimeSpent: false,
      errorTimeSpent: null,

      progressBar: [],
      isLoadingProgressBar: false,
      errorProgressBar: null,
      refreshedProgressBar: true,

      leadersBoardText: "",
      leadersBoard: [],
      isLoadingLeadersBoard: false,
      errorLeadersBoard: null,
      refreshedLeaderBoard: true,

      pioneersClub: [],
      isLoadingPioneersClub: false,
      errorPioneersClub: null,
    }),
}));

export default useStatisticsStore;
